import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../../utils/toast";
import { Warehouse, ListChecks, PackageCheck, AlertTriangle, XCircle } from "lucide-react";
import useAuth from "../../../hooks/useAuth";
import { PageHeader } from "../../../components/ui/PageHeader";
import { PendingBackendNotice } from "../../../components/ui/PendingBackendNotice";
import { KpiFilterCards } from "../../../components/ui/KpiFilterCards";
import { Select } from "../../../components/ui/Select";

import ScanConsole from "./ScanConsole";
import SessionSummary from "./SessionSummary";
import SessionList from "./SessionList";
import OrderDetailDrawer from "./OrderDetailDrawer";
import FinishSessionModal from "./FinishSessionModal";
import { playBeep } from "./beep";
import {
  BODEGAS, CLIENTS, makeInitialState, findOrder, comercioName,
  recipientName, STATUS_PENDIENTE, STATUS_RECOLECTADO,
} from "./mockData";

const clock = (ts) => {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const fmtElapsed = (ms) => {
  const m = Math.max(0, Math.floor(ms / 60000));
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${m % 60}m` : `${m}m`;
};

export default function RecepcionBodega() {
  const { t } = useTranslation();
  const { user, userData } = useAuth();

  const [bootstrap] = useState(() => makeInitialState(Date.now()));
  const [orders, setOrders] = useState(bootstrap.orders);
  const [session, setSession] = useState(bootstrap.session);
  const [startedAt, setStartedAt] = useState(bootstrap.startedAt);
  const [lastResult, setLastResult] = useState(bootstrap.lastResult);
  const nextIdRef = useRef(bootstrap.nextId);

  const [mode, setMode] = useState("scanner");
  const [query, setQuery] = useState("");
  const [muted, setMuted] = useState(false);
  const [bodegaId, setBodegaId] = useState(BODEGAS[0].id);
  const [clientId, setClientId] = useState("");
  const [filter, setFilter] = useState("");
  const [drawerOrder, setDrawerOrder] = useState(null);
  const [finishOpen, setFinishOpen] = useState(false);
  const [correlativo, setCorrelativo] = useState("");
  const [now, setNow] = useState(Date.now());

  const inputRef = useRef(null);
  const refocus = () => requestAnimationFrame(() => inputRef.current?.focus());

  // reloj para tiempo activo / ritmo
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 20000);
    return () => clearInterval(id);
  }, []);

  // mantener el foco en el input cuando estamos en modo pistola
  useEffect(() => {
    if (mode === "scanner" && !drawerOrder && !finishOpen) refocus();
  }, [mode, drawerOrder, finishOpen]);

  const operatorName =
    [userData?.name || user?.name, userData?.lastName || user?.lastName].filter(Boolean).join(" ") || t("Operador");
  const bodega = BODEGAS.find((b) => b.id === bodegaId) || BODEGAS[0];
  const client = CLIENTS.find((c) => c.id === clientId) || null;
  const clientName = client?.name || "";

  const ordersById = useMemo(() => {
    const m = {};
    orders.forEach((o) => { m[o._id] = o; });
    return m;
  }, [orders]);

  // conteos por resultado (already cuenta dentro de "repetidos")
  const breakdown = useMemo(() => {
    const b = { success: 0, duplicate: 0, notfound: 0 };
    session.forEach((s) => {
      if (s.outcome === "success") b.success += 1;
      else if (s.outcome === "notfound") b.notfound += 1;
      else b.duplicate += 1; // duplicate + already
    });
    return b;
  }, [session]);

  const manualMatches = useMemo(() => {
    if (mode !== "manual" || !query.trim()) return [];
    const q = query.trim().toLowerCase();
    return orders.filter(
      (o) =>
        o.status === STATUS_PENDIENTE &&
        (!clientName || comercioName(o) === clientName) &&
        (o.followingNumber.includes(q) ||
          o.order_id.toLowerCase().includes(q) ||
          recipientName(o).toLowerCase().includes(q) ||
          comercioName(o).toLowerCase().includes(q)),
    );
  }, [mode, query, orders, clientName]);

  const filteredItems = useMemo(() => {
    if (!filter) return session;
    if (filter === "duplicate") return session.filter((s) => s.outcome === "duplicate" || s.outcome === "already");
    return session.filter((s) => s.outcome === filter);
  }, [session, filter]);

  // ── acción central: procesar un código ───────────────────────────────────
  const processScan = (raw) => {
    const code = (raw || "").trim();
    if (!code) return;

    const order = findOrder(orders, code);
    const ts = Date.now();
    let outcome;
    let orderId = null;
    let mismatch = false;

    if (!order) {
      outcome = "notfound";
    } else {
      orderId = order._id;
      const alreadyScanned = session.some((s) => s.orderId === order._id && s.outcome === "success");
      if (alreadyScanned) {
        outcome = "duplicate";
      } else if (order.status !== STATUS_PENDIENTE) {
        outcome = "already";
      } else {
        outcome = "success";
        // pertenece a otro comercio que el cliente asignado a la recepción
        mismatch = !!clientName && comercioName(order) !== clientName;
        setOrders((prev) =>
          prev.map((o) => (o._id === order._id ? { ...o, status: STATUS_RECOLECTADO, _recibidoAt: ts } : o)),
        );
      }
    }

    const shownCode = order ? order.followingNumber : code;
    const item = { id: nextIdRef.current++, ts, code: shownCode, outcome, orderId, mismatch };
    setSession((prev) => [item, ...prev]);
    setLastResult({ outcome, code: shownCode, orderId, ts, mismatch });
    if (!muted) playBeep(mismatch ? "duplicate" : outcome);

    if (outcome === "success" && mismatch)
      toast.warn(t("#{{code}} recolectado, pero es de {{comercio}}", { code: shownCode, comercio: comercioName(order) }));
    else if (outcome === "success") toast.success(t("#{{code}} recolectado", { code: shownCode }));
    else if (outcome === "duplicate") toast.warn(t("#{{code}} ya estaba en esta sesión", { code: shownCode }));
    else if (outcome === "already") toast.info(t("#{{code}} ya estaba recolectado", { code: shownCode }));
    else toast.error(t("Código no encontrado: {{code}}", { code: shownCode }));

    setQuery("");
    refocus();
  };

  const handlePickManual = (order) => {
    setQuery("");
    processScan(order.followingNumber);
  };

  const undoItem = (item) => {
    if (item.outcome !== "success") return;
    if (item.orderId) {
      setOrders((prev) =>
        prev.map((o) => (o._id === item.orderId ? { ...o, status: STATUS_PENDIENTE, _recibidoAt: undefined } : o)),
      );
    }
    setSession((prev) => prev.filter((s) => s.id !== item.id));
    setLastResult((prev) => (prev && prev.ts === item.ts ? null : prev));
    toast.info(t("Recepción deshecha: #{{code}}", { code: item.code }));
    refocus();
  };

  const undoLast = () => {
    const last = session.find((s) => s.outcome === "success");
    if (last) undoItem(last);
  };

  const openFinish = () => {
    // correlativo simulado del retiro
    const n = String(400 + breakdown.success).padStart(4, "0");
    setCorrelativo(`R-2026-${n}`);
    setFinishOpen(true);
  };

  const confirmFinish = () => {
    setSession([]);
    setLastResult(null);
    setStartedAt(Date.now());
    setNow(Date.now());
    setFilter("");
    setFinishOpen(false);
    toast.success(t("Sesión cerrada. Nueva recepción iniciada."));
    refocus();
  };

  const byComercio = useMemo(() => {
    const map = new Map();
    session.forEach((s) => {
      if (s.outcome !== "success") return;
      const o = s.orderId ? ordersById[s.orderId] : null;
      const name = o ? comercioName(o) : t("Sin comercio");
      map.set(name, (map.get(name) || 0) + 1);
    });
    return [...map.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
  }, [session, ordersById, t]);

  const mismatchCount = useMemo(
    () => session.filter((s) => s.mismatch && s.outcome === "success").length,
    [session],
  );

  const lastOrder = lastResult?.orderId ? ordersById[lastResult.orderId] : null;
  const activeMin = Math.max(1, (now - startedAt) / 60000);
  const ritmo = breakdown.success > 0 ? (breakdown.success / activeMin).toFixed(1) : "0.0";

  const kpiCards = [
    { value: "", label: t("Todos"), icon: ListChecks, tone: "brand" },
    { value: "success", label: t("Recolectados"), icon: PackageCheck, tone: "success" },
    { value: "duplicate", label: t("Repetidos"), icon: AlertTriangle, tone: "warning" },
    { value: "notfound", label: t("No encontrados"), icon: XCircle, tone: "error" },
  ];

  return (
    <div className="w-full min-w-0 space-y-4 py-2">
      <PageHeader
        icon={<Warehouse className="w-6 h-6 text-fg-muted" />}
        title={t("Recepción en bodega")}
        subtitle={t("Registra los pedidos que los clientes dejan en tu bodega — con pistola de código de barras o a mano, sin la app.")}
        actions={
          <div className="w-full sm:w-64">
            <Select
              aria-label={t("Bodega de recepción")}
              value={bodegaId}
              onChange={(e) => setBodegaId(e.target.value)}
              options={BODEGAS.map((b) => ({ value: b.id, label: b.name }))}
            />
          </div>
        }
      />

      <PendingBackendNotice>
        {t("Vista de demostración: los datos son simulados. El catálogo de bodegas y el registro del retiro están en construcción.")}
      </PendingBackendNotice>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        <div className="lg:col-span-2">
          <ScanConsole
            mode={mode}
            onModeChange={setMode}
            query={query}
            onQueryChange={setQuery}
            onSubmit={processScan}
            lastResult={lastResult}
            lastOrder={lastOrder}
            onOpenDetail={setDrawerOrder}
            listening={mode === "scanner"}
            manualMatches={manualMatches}
            onPickManual={handlePickManual}
            inputRef={inputRef}
            clients={CLIENTS}
            clientId={clientId}
            onClientChange={setClientId}
          />
        </div>
        <div className="lg:col-span-1">
          <SessionSummary
            total={session.length}
            breakdown={breakdown}
            ritmo={ritmo}
            elapsedLabel={fmtElapsed(now - startedAt)}
            bodegaName={bodega.name}
            clientName={clientName}
            operatorName={operatorName}
            startLabel={clock(startedAt)}
            muted={muted}
            onToggleMute={() => setMuted((m) => !m)}
            onUndoLast={undoLast}
            canUndo={session.some((s) => s.outcome === "success")}
            onFinish={openFinish}
            canFinish={session.length > 0}
          />
        </div>
      </div>

      <KpiFilterCards
        cards={kpiCards}
        counts={{ success: breakdown.success, duplicate: breakdown.duplicate, notfound: breakdown.notfound }}
        total={session.length}
        active={filter}
        onChange={setFilter}
      />

      <SessionList
        items={filteredItems}
        ordersById={ordersById}
        onOpenDetail={setDrawerOrder}
        onUndo={undoItem}
        hasAny={session.length > 0}
        filter={filter}
        onResetFilter={() => setFilter("")}
      />

      <OrderDetailDrawer
        open={!!drawerOrder}
        onClose={() => setDrawerOrder(null)}
        order={drawerOrder}
        recibidoAt={drawerOrder?._recibidoAt}
        bodegaName={bodega.name}
      />

      <FinishSessionModal
        open={finishOpen}
        onClose={() => setFinishOpen(false)}
        onConfirm={confirmFinish}
        onDownload={() => toast.info(t("Generación de comprobante en construcción."))}
        total={breakdown.success}
        breakdown={breakdown}
        byComercio={byComercio}
        bodegaName={bodega.name}
        clientName={clientName}
        mismatchCount={mismatchCount}
        operatorName={operatorName}
        periodLabel={`${clock(startedAt)}–${clock(now)}`}
        correlativo={correlativo}
      />
    </div>
  );
}
